import { OnboardingDocumentType } from '@dtos/document-type';
import { RemoteI9Details } from '@dtos/remote-i9-details';
import {
    UpdatedDocumentDetails,
    UpdatedDocumentNumbers
} from '@dtos/update-documents';
import Joi from '@hapi/joi';
import { OnboardingDetailsRequestEvent } from '@models/onboarding-review/onboarding-details-request-event';
import { ISection2DocumentDetails } from '@models/onboarding-review/section2-document-details';
import { Section2ValidationErrors } from '@models/onboarding-review/section2-validation-errors';
import { IDocumentUpdateApiService } from '@services/document-update-api-service';
import { IOnboardingDetailsRequestService } from '@services/onboarding-review/onboarding-details-request-service';
import { IValidateDocumentDetailService } from '@services/validate-document-detail-service';
import { ISection2DocumentSubmissionStore } from '@stores/onboarding-review/section2-document-submission-store';
import { ISection2DocumentsStore } from '@stores/onboarding-review/section2-documents-store';
import { chainBooleanTasks } from '@utils/chain-boolean-tasks';
import { getLocalTimezone } from '@utils/date-query';
import { getZonedDateAsMidnightUTC } from '@utils/date-transformation';
import { ISection2DocumentTypesValidation } from '@stores/onboarding-review/section2-document-types-validation-store';
import {
    forEach,
    fromPairs,
    isEqual,
    mapValues,
    pick
} from 'lodash';
import { Subscription } from 'rxjs';
import { IPersonPidEncryptionService } from './person-pid-encryption-service';

type DocumentDetailsValidationErrors = Pick<Record<Section2ValidationErrors, boolean>, Section2ValidationErrors.documentDetailMissing>;

export interface IDocumentDetailsChangeTrackingService {
    initialise(): void;
    cleanup(): void;
    collectDocumentDetails(): UpdatedDocumentDetails;
    collectDocumentNumbers(): UpdatedDocumentNumbers;
    getOriginalDocumentDetails(): ISection2DocumentDetails[];
    getChangedDocumentDetails(): ISection2DocumentDetails[];
    getChangedDocumentNumbers(): ISection2DocumentDetails[];
    isRequestValid(): boolean;
    submitChangesAsync(): Promise<boolean>;
    getValidationErrors(): DocumentDetailsValidationErrors;
}

const DETAILS_TO_COMPARE: Array<keyof ISection2DocumentDetails> = [
    'documentType',
    'expiryDate',
    'issuingAuthority',
    'issuingAuthorityCode'
];

const DETAILS_TO_SEND: Array<keyof ISection2DocumentDetails> = [
    ...DETAILS_TO_COMPARE,
    'documentId'
];

export class DocumentDetailsChangeTrackingService implements IDocumentDetailsChangeTrackingService {
    /**
     * Collection of the original document details keyed by document type
     * @private
     */
    private documents: Map<string, ISection2DocumentDetails> = new Map<string, ISection2DocumentDetails>();
    private requestEventsSubscription: Subscription = Subscription.EMPTY;

    constructor(
        private documentStore: ISection2DocumentsStore,
        private onboardingDetailsRequestService: IOnboardingDetailsRequestService,
        private personPidEncryptionService: IPersonPidEncryptionService,
        private documentUpdateApiService: IDocumentUpdateApiService,
        private validateDocumentDetailService: IValidateDocumentDetailService,
        private section2DocumentSubmissionStore: ISection2DocumentSubmissionStore
    ) {
    }

    initialise(): void {
        this.requestEventsSubscription = this.onboardingDetailsRequestService
            .requestEvents
            .subscribe(this.handleOnboardingDetailsEvents);
    }

    handleOnboardingDetailsEvents = (event: OnboardingDetailsRequestEvent) => {
        if (event.type === 'success') {
            this.handleSuccess(event.dto);
            return;
        }

        if (event.type === 'pending') {
            this.handlePending();
            return;
        }
    };

    cleanup(): void {
        this.clearInternalState();
        this.requestEventsSubscription.unsubscribe();
    }

    handlePending = () => {
        this.clearInternalState();
        this.section2DocumentSubmissionStore.setLastSubmittedDocumentNumbers({});
    };

    handleSuccess = ({ documentDetails }: RemoteI9Details) => {
        forEach(documentDetails, details => {
            const converted = {
                ...details,
                expiryDate: details.expiryDate || null,
                number: this.personPidEncryptionService.decryptDto(details.number)
            };

            this.documents.set(converted.documentType, converted);
        });
        this.section2DocumentSubmissionStore.setLastSubmittedDocumentNumbers({});
    };

    getChangedDocumentDetails(): ISection2DocumentDetails[] {
        return this.getChangedDocuments(DETAILS_TO_COMPARE);
    }

    getChangedDocumentNumbers(): ISection2DocumentDetails[] {
        return this.getChangedDocuments(['number']);
    }

    /**
     * When updating the details, we need to send all the documents including our changes
     */
    collectDocumentDetails(): UpdatedDocumentDetails {
        const allDocumentsWithChanges = this.documentStore.allDocuments;
        return fromPairs(
            allDocumentsWithChanges.map(detail => {
                const detailsToSend = pick(detail, ...DETAILS_TO_SEND);
                const hasExpiryDateChanged = detailsToSend.expiryDate !== this.documents.get(detail.documentType)?.expiryDate;
                const getAdjustedExpiryDate = (expiryDate: string | null) => {
                    if (expiryDate === null) {
                        return null;
                    }

                    return getZonedDateAsMidnightUTC(
                        expiryDate,
                        hasExpiryDateChanged ? getLocalTimezone() : 'Etc/UTC'
                    );
                };
                return [
                    detail.documentId,
                    {
                        ...detailsToSend,
                        expiryDate: getAdjustedExpiryDate(detailsToSend.expiryDate),
                        expiryDateNAChecked: detail.isExpiryNaCheckboxChecked
                    }
                ];
            })
        );
    }

    /**
     * When updating the document numbers, we need to send only the front documents
     * then encrypt them
     * @throws if there was an issue encrypting a document number change
     */
    collectDocumentNumbers(): UpdatedDocumentNumbers {
        return mapValues(
            this.getDocumentNumbersForSubmission(),
            sanitisedNumber => {
                return this.personPidEncryptionService.encryptDto(sanitisedNumber)
            }
        );
    }

    /**
     * Retrieves all front document numbers for submission
     * @private
     */
    private getDocumentNumbersForSubmission(): Record<string, string> {
        const frontDocuments = [
            ...this.documentStore.listA,
            ...this.documentStore.listB,
            ...this.documentStore.listC
        ];

        return fromPairs(
            frontDocuments.map((
                {
                    documentId,
                    documentType,
                    number: documentNumber
                }
            ) => [
                documentId,
                this.validateDocumentDetailService.sanitiseDocumentNumberForSubmission(
                    documentType,
                    documentNumber
                )
            ])
        );
    }

    private validateDocumentDetails(documentDetails: ISection2DocumentDetails, validationOptions?: Joi.ValidationOptions): Joi.ValidationResult {
        const mustBeNonEmptyString = Joi.string()
            .required()
            .min(1);
        const schema = Joi.object<any, ISection2DocumentDetails>({
            documentId: mustBeNonEmptyString,
            documentType: mustBeNonEmptyString,
            issuingAuthority: mustBeNonEmptyString,
            number: mustBeNonEmptyString,
            expiryDate: Joi.string()
                .min(1)
                .required()
                .isoDate()
                .allow(null),
                isExpiryNaCheckboxChecked: Joi.boolean() || undefined,
                issuingAuthorityCode: Joi.string().allow(null, '') || null || undefined,
                expiryDateNAChecked: Joi.boolean() || undefined
        });

        return schema.validate({...documentDetails}, validationOptions);
    }

    getValidationErrors(): DocumentDetailsValidationErrors {
        const editableFieldChanges = this.getChangedDocuments([
            'expiryDate',
            'issuingAuthority',
            'number',
            'issuingAuthorityCode'
        ]);

        const documentDetailsErrors = editableFieldChanges
            .map(details => this.validateDocumentDetails(details))
            .filter(result => result.error !== undefined);
        return {
            [Section2ValidationErrors.documentDetailMissing]: documentDetailsErrors.length > 0
        };
    }

    isRequestValid(): boolean {
        const frontDocumentsWithChanges = [
            ...this.documentStore.listA,
            ...this.documentStore.listB,
            ...this.documentStore.listC
        ].map(details => ({
            ...this.documents.get(details.documentType) ?? {},
            ...details
        }));
        return frontDocumentsWithChanges.every(details => {
            const schemaResult = this.validateDocumentDetails(details);
            const documentDetails: ISection2DocumentTypesValidation | undefined =
            this.documentStore.documentTypesValidationsAll.find(
          (item: ISection2DocumentTypesValidation) =>
            item.documentType === details.documentType
        );

            const validationResult = this.validateDocumentDetailService.validateDocumentNumber(
                details.documentType as OnboardingDocumentType,
                details.number,
                documentDetails
            );
            return schemaResult.error === undefined && validationResult.valid;
        });
    }

    getOriginalDocumentDetails(): ISection2DocumentDetails[] {
        return Array.from(this.documents.values());
    }

    /**
     * Submits the changes if made, to details and or document numbers
     */
    submitChangesAsync(): Promise<boolean> {
        const changedDocumentDetails = this.getChangedDocumentDetails();

        return chainBooleanTasks(
            () => {
                if (changedDocumentDetails.length === 0) {
                    return Promise.resolve(true);
                }

                return this.documentUpdateApiService.updateDocumentDetailsAsync({
                    documentDetails: this.collectDocumentDetails()
                });
            },
            async () => {
                const documentNumbersForSubmission = this.getDocumentNumbersForSubmission();
                this.section2DocumentSubmissionStore.setLastSubmittedDocumentNumbers(
                    documentNumbersForSubmission
                );

                return this.documentUpdateApiService.updateDocumentNumbersAsync({
                    documentNumbers: this.collectDocumentNumbers()
                });
            }
        );
    }

    private getChangedDocuments(compareFields: Array<keyof ISection2DocumentDetails>): ISection2DocumentDetails[] {
        return this.documentStore.allDocuments.filter(details => {
            const original = this.documents.get(details.documentType);
            if (!original) {
                // there should not be a change for a document that does not have an original
                // ignore update if found
                return false;
            }

            return !isEqual(
                pick(original, compareFields),
                pick(details, compareFields)
            );
        });
    }

    private clearInternalState() {
        this.documents.clear();
    }
}



// WEBPACK FOOTER //
// ./src/services/onboarding-review/document-details-change-tracking-service.ts